import { useTranslations } from 'next-intl';
import type { ComponentType } from 'react';
import { BaseLineInfo } from '@/app/components/icons/BaseLineInfo';
import { PostReferenceFilter } from '@/app/components/shared/PostReferenceFilter';
import { SearchInput } from '@/app/components/shared/SearchInput';
import { SettingRadioGroup } from '@/app/components/shared/SettingRadioGroup';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/app/components/ui/tabs';
import { SORT_ICON_MAP } from '@/app/constants';
import type { ElementModality, ElementType, SortByType, TagType } from '@/app/types';
import { cn, reportEvent } from '@/app/utils';
import type { useElementSelector } from '../hooks/use-element-selector';
import { type ElementListProps, HEAD_TAB } from '../types';
import { LabelFilter } from './LabelFilter';

/**
 * 从 useElementSelector 推断返回类型
 */
type ElementSelectorHookReturn = ReturnType<typeof useElementSelector>;

interface ElementSelectorTabsProps {
  // Tab 相关
  activeHeadTab: HEAD_TAB;
  onHeadTabChange: (value: string) => void;
  headTabs: Array<{ id?: string; text: string; key: HEAD_TAB }>;

  // 搜索相关
  searchValue: string;
  handleSetSearchValue: (value: string) => void;

  // 标签相关
  tags: TagType[] | undefined;
  selectedTags: string[];
  handleTagSelectorChange: (id: string) => void;
  deleteTag: (id: string) => void;

  // 元素相关
  elements: ElementType[];
  selectedElements: ElementType[];
  toggleElementSelection: (element: ElementType) => void;
  disabledIds?: string[];

  // 个人元素列表相关
  elementsListReq: ElementSelectorHookReturn['elementsListReq'];

  // 社区分享列表相关
  categories?: TagType[];
  activeCategory: string;
  setActiveCategory: (id: string) => void;
  sortBy: SortByType;
  setSortBy: (sortBy: SortByType) => void;
  sortOptions: Array<{ label: string; value: SortByType }>;
  postReferenceListReq: ElementSelectorHookReturn['postReferenceListReq'];
  getShareReferenceData: () => void;
  onExpose: (idx: number) => void;

  // ElementList 组件
  ElementListComponent: ComponentType<ElementListProps>;
  allowedModalities?: Array<ElementModality>;
  onCreateOpen?: () => void;
}

/**
 * Elements Selector Tabs 组件
 * 包含"我的元素"和"社区分享"两个 Tab 的内容
 */
export function ElementSelectorTabs(props: ElementSelectorTabsProps) {
  const {
    activeHeadTab,
    onHeadTabChange,
    headTabs,
    searchValue,
    handleSetSearchValue,
    tags,
    selectedTags,
    handleTagSelectorChange,
    deleteTag,
    elements,
    selectedElements,
    toggleElementSelection,
    disabledIds,
    elementsListReq,
    categories,
    activeCategory,
    setActiveCategory,
    sortBy,
    setSortBy,
    sortOptions,
    postReferenceListReq,
    getShareReferenceData,
    onExpose,
    ElementListComponent,
    allowedModalities,
    onCreateOpen,
  } = props;

  const t = useTranslations();
  const allowedModalitiesLabel =
    allowedModalities?.map((modality) => t(`elements.elementDialog.modality.${modality}`)).join(' / ') ?? '';

  return (
    <Tabs value={activeHeadTab} className="flex h-full flex-col" onValueChange={onHeadTabChange}>
      <TabsList className="flex h-fit w-fit gap-6 bg-none bg-transparent p-0">
        {headTabs.map((it) => (
          <TabsTrigger
            key={it.key}
            value={it.key}
            id={it.id}
            className={cn(
              'relative px-0 py-1 font-normal text-sm text-system-white64 hover:bg-transparent',
              activeHeadTab === it.key &&
                'before:-left-[1px] before:-right-[1px] font-medium text-system-white before:absolute before:bottom-0 before:h-[2px] before:rounded-full before:bg-system-white before:content-[""]',
            )}
          >
            {it.text}
          </TabsTrigger>
        ))}
        {allowedModalities?.length && (
          <div className="flex items-center justify-center gap-1 text-xs">
            <BaseLineInfo className="size-3 text-system-white64" />
            {t('elements.elementDialog.showModalities.others', { modalities: allowedModalitiesLabel })}
          </div>
        )}
      </TabsList>

      {/* 我的元素 Tab */}
      {activeHeadTab === HEAD_TAB.REFERENCE && (
        <TabsContent value={HEAD_TAB.REFERENCE} className="flex flex-1 flex-col">
          <div className="flex w-full flex-1 flex-col gap-4 overflow-y-auto rounded-lg text-white">
            <div className="mt-4">
              <div className="flex justify-end">
                <SearchInput
                  className="w-full"
                  onSearch={handleSetSearchValue}
                  placeholder={t('shareReference.search.placeholder.explore')}
                  inputClassName="focus-visible:bg-system-white10 pe-8 border-transparent h-6 text-xs"
                  maxLength={60}
                />
              </div>

              <LabelFilter
                className="mt-2 transition-all duration-300"
                tags={tags || []}
                tagSelections={selectedTags}
                handleTagSelectorChange={handleTagSelectorChange}
                onDelete={deleteTag}
              />
            </div>

            <ElementListComponent
              tagSelectors={selectedTags}
              toggleElementSelection={toggleElementSelection}
              selectedElements={selectedElements}
              className={cn('flex-1 overflow-hidden')}
              disabledIds={disabledIds}
              tags={tags}
              elements={elements}
              isLoading={elementsListReq.loading}
              hasNextPage={!!elementsListReq.data?.data?.next_page_token}
              onLoadMore={elementsListReq.run}
              enableCreate={!searchValue}
              onCreateOpen={onCreateOpen}
            />
          </div>
        </TabsContent>
      )}

      {/* 社区分享 Tab */}
      {activeHeadTab === HEAD_TAB.SHARE_REFERENCE && (
        <TabsContent value={HEAD_TAB.SHARE_REFERENCE} className="flex-1 overflow-hidden">
          <div id="element-selector-scroll" className="flex h-full flex-col overflow-y-auto rounded-lg text-white">
            <div className="mt-4 flex justify-between">
              <SearchInput
                className="w-full"
                onSearch={handleSetSearchValue}
                placeholder={t('shareReference.search.placeholder.explore')}
                inputClassName="focus-visible:bg-system-white10 pe-8 border-transparent h-6 text-xs"
                maxLength={60}
              />
            </div>

            <PostReferenceFilter
              categories={categories}
              activeCategory={activeCategory}
              setActiveCategory={setActiveCategory}
              className="mt-3 mb-4"
              size="small"
            />

            <SettingRadioGroup
              options={sortOptions}
              className="mb-4 w-fit rounded-8 bg-system-white10"
              activeOptionClassName="border-none bg-[#364555]"
              value={sortBy}
              onValueChange={(val) => {
                reportEvent('reference_sort_click', {
                  name: (val as SortByType) === 'recommend' ? 'recommend' : 'new',
                });
                setSortBy(val as SortByType);
              }}
              optionsWrapperClassName="h-6"
              renderContent={(option) => {
                return (
                  <img
                    src={SORT_ICON_MAP[option.value as SortByType]}
                    alt={option.label?.toString() || ''}
                    className="h-2 w-5"
                  />
                );
              }}
            />

            <ElementListComponent
              toggleElementSelection={toggleElementSelection}
              selectedElements={selectedElements}
              className={cn('flex-1 overflow-hidden')}
              onExpose={onExpose}
              deeps={[sortBy, activeCategory]}
              disabledIds={disabledIds}
              elements={elements}
              isLoading={postReferenceListReq.loading}
              hasNextPage={!!postReferenceListReq.data?.data?.next_page_token}
              onLoadMore={getShareReferenceData}
              onCreateOpen={onCreateOpen}
            />
          </div>
        </TabsContent>
      )}
    </Tabs>
  );
}
